import { z } from 'zod';

import { MoneySchema, TimestampSchema } from './primitives.js';

/**
 * One promotion-application audit record.
 *
 * Stored at
 * `tenants/{tid}/outlets/{oid}/promotionApplications/{ticketId}_{promotionId}`,
 * written by the queue callables (`bookTicket`, `appendItemsToTicket`) in the
 * SAME transaction that stamps `snapshot.appliedPromotions` on the ticket — so
 * the audit row is atomic with the ticket write and needs NO per-ticket trigger
 * (avoids an extra function invocation per booking).
 *
 * The doc id is deterministic (`${ticketId}_${promotionId}`) so a re-resolution
 * (an upsell `appendItemsToTicket`) or a transaction retry UPSERTs the same doc
 * instead of duplicating — exactly one row per (ticket, promotion) pair.
 *
 * Purpose: a cross-ticket trail of which promos were applied, on what ticket,
 * for how much — the operator read surface on `/loyalty/promotions`.
 * Server-managed (writes server-only via the admin SDK); clients read-only,
 * scoped to owner/manager/cashier of the tenant (`infra/firestore.rules`).
 */
export const PromotionApplicationSchema = z.object({
  id: z
    .string()
    .describe('Doc id — `${ticketId}_${promotionId}` (deterministic, idempotent upsert key)'),
  tenantId: z.string().describe('Owning tenant id'),
  outletId: z.string().describe('Owning outlet id — the promotion applied at this outlet'),
  ticketId: z.string().describe('Ticket the promotion was applied to'),
  ticketNumber: z
    .string()
    .describe('Human-facing short ticket number (e.g. "A042"), snapshotted for the audit list'),
  customerId: z
    .string()
    .nullable()
    .describe('Customer uid on the ticket, or null for a walk-in with no attached account'),
  promotionId: z.string().describe('The applied promotion id (`tenants/{tid}/promotions/{id}`)'),
  promotionName: z
    .string()
    .optional()
    .describe('English promotion name at apply time, snapshotted so the list needs no join'),
  savingsSen: MoneySchema.describe(
    'Discount this promotion contributed to the ticket, integer sen',
  ),
  appliedAt: TimestampSchema.describe('When the promotion was applied (ticket book / append time)'),
});
export type PromotionApplication = z.infer<typeof PromotionApplicationSchema>;
